import { surveyQuestionHelper } from '../../helpers/surveyQuestionHelper';
import { DefaultSurveyQuestionOptions, SurveyQuestion } from './SurveyQuestion';

export type SelectionSummary = {

    optionValueCounts: {
        value:string,
        count:number
    }[]
}

export class SelectionSummaryWrapper
{
    data:SelectionSummary;

    constructor(data:SelectionSummary)
    {
        if(!data)
            throw new Error('SelectionSummaryWrapper requires a valid SelectionSummary object');

        this.data = data;
    }

    getTotalSelections():number
    {
        return this.data.optionValueCounts.reduce((acc, ovc) => acc + ovc.count, 0);
    }

    getOptionOrNull(value:string):{ value:string, count:number } | null
    {
        return this.data.optionValueCounts.find(o => o.value === value) || null;
    }

    getOption(value:string):{ value:string, count:number }
    {
        const option = this.getOptionOrNull(value);
        if(!option)
            throw new Error(`Invalid option value ${value}`);

        return option;
    }

    setOptionCount(value:string, count:number, addIfMissing?:boolean)
    {
        const option = this.getOptionOrNull(value);
        if(option)
            option.count = count;
        else {
            if(addIfMissing)
                this.data.optionValueCounts.push({ value, count });
            else
                throw new Error(`Invalid option value ${value}`);
        }
        return this;
    }

    setDefaultOptionCount(value:'1' | '2' | '3' | '4' | '5', count:number, addIfMissing?:boolean):SelectionSummaryWrapper
    {
        return this.setOptionCount(value, count, addIfMissing);
    }

    static createDefault():SelectionSummaryWrapper
    {
        return new SelectionSummaryWrapper({

            optionValueCounts: DefaultSurveyQuestionOptions.map(o => ({ value:o.value, count:0 }))
        });
    }

    static createFromQuestion(surveyQuestion:SurveyQuestion):SelectionSummaryWrapper
    {
        const options = surveyQuestionHelper.getOptions(surveyQuestion);
        return new SelectionSummaryWrapper({
            optionValueCounts: options.map(o => ({ value:o.value, count:0 }))
        });
    }

    static fromData(data:SelectionSummary):SelectionSummaryWrapper
    {
        return new SelectionSummaryWrapper(data);
    }

    static fromDefaultCounts(value1Count:number, value2Count:number, value3Count:number, value4Count:number, value5Count:number):SelectionSummaryWrapper
    {
        const wrapper = new SelectionSummaryWrapper({
            optionValueCounts:[
                { value:'1', count:value1Count },
                { value:'2', count:value2Count },
                { value:'3', count:value3Count },
                { value:'4', count:value4Count },
                { value:'5', count:value5Count }
            ]
        });
        return wrapper;
    }

    static aggregate(summaries:SelectionSummary[]):SelectionSummaryWrapper
    {
        if(summaries.length === 0)
            throw new Error('No summaries to aggregate');

        // Copy the first summary to avoid mutating the original object
        const combined:SelectionSummary = {
            optionValueCounts:summaries[0].optionValueCounts.map(ovc => ({ value:ovc.value, count:ovc.count }))
        };

        for(let i=1; i<summaries.length; i++)
        {
            if(combined.optionValueCounts.length !== summaries[i].optionValueCounts.length)
                throw new Error(`Mismatched option valueCountLengths ${JSON.stringify(combined.optionValueCounts)} and ${JSON.stringify(summaries[i].optionValueCounts)} during SelectionSummary aggregation`);

            for(const ovc of summaries[i].optionValueCounts){
                const matchingValue = combined.optionValueCounts.find(covc => covc.value === ovc.value);
                if(!matchingValue)
                    throw new Error('Mismatched option values');

                matchingValue.count += ovc.count;

            }
        }

        return SelectionSummaryWrapper.fromData(combined);
    }
}